import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { FaArrowLeft, FaUser, FaBook, FaGraduationCap, FaCheck, FaTimes, FaClock } from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchDutyStudentDetails, clearDutyStudentDetails, clearDutyStudentDetailsError } from "../../redux/slices/dutyStudentDetailsSlice";
import styles from "./DutyDetails.module.css";

function DutyDetails() {
  const { isDarkMode } = useDarkMode();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const { dutyDetails, loading, error } = useSelector((state) => state.dutyStudentDetails);
  const [studentId, setStudentId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Get student_id from URL query parameters
    const urlParams = new URLSearchParams(location.search);
    const studentIdParam = urlParams.get('student_id');
    setStudentId(studentIdParam);
    
    if (studentIdParam) {
      dispatch(clearDutyStudentDetails());
      dispatch(fetchDutyStudentDetails({ lessonId: id, studentId: studentIdParam }));
    }
  }, [dispatch, id, location.search]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearDutyStudentDetailsError());
    }
  }, [error, dispatch]);

  const formatTime = (seconds) => {
    if (!seconds) return 'غير محدد';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const calculatePercentage = (studentDegree, totalDegree) => {
    if (totalDegree === 0) return 0;
    return Math.round((studentDegree / totalDegree) * 100);
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 90) return '#10b981'; // Green
    if (percentage >= 70) return '#f59e0b'; // Yellow
    if (percentage >= 50) return '#f97316'; // Orange
    return '#ef4444'; // Red
  };

  if (loading) {
    return (
      <div className={`${styles['duty-details-page']} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`}>
        <div className="container">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
            <p className="mt-3">جاري تحميل تفاصيل الواجب...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!dutyDetails) {
    return (
      <div className={`${styles['duty-details-page']} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`}>
        <div className="container">
          <div className="text-center py-5">
            <h3>لا يمكن تحميل تفاصيل الواجب</h3>
            <p>لم يتم العثور على تفاصيل الواجب للطالب المحدد</p>
            <button className="btn btn-primary mt-3" onClick={() => navigate(`/duty-records/${id}`)}>
              العودة لسجل الواجبات
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles['duty-details-page']} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`}>
      <div className="container">
        {/* Header */}
        <div className={styles['duty-details-header']}>
          <button 
            className={styles['back-btn']}
            onClick={() => navigate(`/duty-records/${id}`)}
          >
            <FaArrowLeft />
            رجوع
          </button>
          
          <div className={styles['duty-details-title']}>
            <h1>تفاصيل الواجب</h1>
            <p>عرض إجابة الطالب على الواجب</p>
          </div>
        </div>

        {/* Lesson Information */}
        <div className={styles['lesson-info-section']}>
          <div className={styles['lesson-card']}>
            <div className={styles['lesson-header']}>
              <h3>معلومات الحصة</h3>
            </div>
            
            <div className={styles['lesson-details']}>
              <div className={styles['lesson-item']}>
                <span className={styles['lesson-label']}>عنوان الحصة:</span>
                <span className={styles['lesson-value']}>{dutyDetails.lesson_title || 'غير محدد'}</span>
              </div>
              <div className={styles['lesson-item']}>
                <span className={styles['lesson-label']}>المادة:</span>
                <span className={styles['lesson-value']}>
                  <FaBook className="me-1" />
                  {dutyDetails.subject || 'غير محدد'}
                </span>
              </div>
              <div className={styles['lesson-item']}>
                <span className={styles['lesson-label']}>معرف الحصة:</span>
                <span className={styles['lesson-value']}>{dutyDetails.lesson_id || 'غير محدد'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Score Summary */}
        <div className={styles['score-summary']}>
          <div className={styles['score-card']}>
            <div className={styles['score-header']}>
              <h3>ملخص النتائج</h3>
            </div>
            
            <div className={styles['score-content']}>
              <div className={styles['score-stats']}>
                <div className={styles['stat-item']}>
                  <span className={styles['stat-label']}>عدد الأسئلة:</span>
                  <span className={styles['stat-value']}>{dutyDetails.questions?.length || 0}</span>
                </div>
                <div className={styles['stat-item']}>
                  <span className={styles['stat-label']}>الدرجة الكلية:</span>
                  <span className={styles['stat-value']}>
                    {dutyDetails.questions?.reduce((total, q) => total + (q.question_degree || 0), 0) || 0}
                  </span>
                </div>
                <div className={styles['stat-item']}>
                  <span className={styles['stat-label']}>الأسئلة المجاب عنها:</span>
                  <span className={styles['stat-value']}>
                    {dutyDetails.questions?.filter(q => q.student_answer !== null).length || 0}
                  </span>
                </div>
                <div className={styles['stat-item']}>
                  <span className={styles['stat-label']}>الأسئلة الصحيحة:</span>
                  <span className={styles['stat-value']}>
                    {dutyDetails.questions?.filter(q => q.is_correct === 1).length || 0}
                  </span>
                </div>
              </div>
              
              <div className={styles['score-breakdown']}>
                <h4>تفصيل النتائج:</h4>
                <div className={styles['breakdown-list']}>
                  {dutyDetails.questions?.map((question, index) => (
                    <div key={question.question_id} className={styles['breakdown-item']}>
                      <span className={styles['question-number']}>السؤال {index + 1}:</span>
                      <span className={styles['question-status']}>
                        {question.student_answer ? (
                          question.is_correct === 1 ? (
                            <span className={styles['correct-status']}>✓ صحيح</span>
                          ) : question.is_correct === 0 ? (
                            <span className={styles['incorrect-status']}>✗ خطأ</span>
                          ) : (
                            <span className={styles['pending-status']}>⏳ في انتظار التصحيح</span>
                          )
                        ) : (
                          <span className={styles['no-answer-status']}>- لم يجيب</span>
                        )}
                      </span>
                      <span className={styles['question-degree']}>
                        ({question.question_degree} درجة)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Questions and Answers */}
        <div className={styles['questions-section']}>
          <div className={styles['questions-header']}>
            <h3>الأسئلة والإجابات</h3>
          </div>
          
          {dutyDetails.questions && dutyDetails.questions.length > 0 ? (
            <div className={styles['questions-list']}>
              {dutyDetails.questions.map((question, questionIndex) => (
                <div key={question.question_id} className={styles['question-card']}>
                  <div className={styles['question-header']}>
                    <h4>السؤال {questionIndex + 1}</h4>
                    <span className={styles['question-degree']}>
                      الدرجة: {question.question_degree}
                    </span>
                  </div>
                  
                  <div className={styles['question-content']}>
                    <div className={styles['question-text']}>
                      <p>{question.question_title}</p>
                    </div>
                    
                    {question.question_image && (
                      <div className={styles['question-image']}>
                        <img src={question.question_image} alt="صورة السؤال" />
                      </div>
                    )}
                  </div>
                  
                  {/* Answers */}
                  <div className={styles['answers-section']}>
                    <h5>الإجابات:</h5>
                    <div className={styles['answers-list']}>
                      {question.answers && question.answers.length > 0 ? (
                        question.answers.map((answer, answerIndex) => (
                          <div 
                            key={answer.answer_id} 
                            className={`${styles['answer-item']} ${
                              answer.is_correct === 1 ? styles['correct'] : 
                              answer.is_correct === 0 ? styles['incorrect'] : 
                              styles['neutral']
                            }`}
                          >
                            <div className={styles['answer-content']}>
                              <span className={styles['answer-letter']}>
                                {String.fromCharCode(65 + answerIndex)}
                              </span>
                              <span className={styles['answer-text']}>
                                {answer.answer_text || 'إجابة فارغة'}
                              </span>
                              <div className={styles['answer-status']}>
                                {answer.is_correct === 1 ? (
                                  <FaCheck className={styles['correct-icon']} title="إجابة صحيحة" />
                                ) : answer.is_correct === 0 ? (
                                  <FaTimes className={styles['incorrect-icon']} title="إجابة خاطئة" />
                                ) : null}
                              </div>
                            </div>
                            
                            {answer.answer_image && (
                              <div className={styles['answer-image']}>
                                <img src={answer.answer_image} alt="صورة الإجابة" />
                              </div>
                            )}
                            
                            {/* Student's Answer Indicator */}
                            {question.student_answer === answer.answer_id && (
                              <div className={styles['student-answer-indicator']}>
                                <span>إجابة الطالب المختارة</span>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className={styles['no-answers']}>
                          <p>لا توجد إجابات متاحة لهذا السؤال</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Question Result */}
                  <div className={styles['question-result']}>
                    {question.student_answer ? (
                      <div className={styles['result-info']}>
                        <span>إجابة الطالب: {question.student_answer}</span>
                        <span className={styles['result-status']}>
                          {question.is_correct === 1 ? (
                            <span className={styles['correct-status']}>✓ صحيح</span>
                          ) : question.is_correct === 0 ? (
                            <span className={styles['incorrect-status']}>✗ خطأ</span>
                          ) : (
                            <span className={styles['neutral-status']}>- لم يتم التصحيح</span>
                          )}
                        </span>
                      </div>
                    ) : (
                      <div className={styles['no-answer']}>
                        <span>لم يجيب الطالب على هذا السؤال</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles['no-questions']}>
              <p>لا توجد أسئلة متاحة</p>
            </div>
          )}
        </div>

        {/* Additional Information */}
        {dutyDetails.additional_info && (
          <div className={styles['additional-info']}>
            <div className={styles['info-card']}>
              <div className={styles['info-header']}>
                <h3>معلومات إضافية</h3>
              </div>
              
              <div className={styles['info-content']}>
                <pre>{JSON.stringify(dutyDetails.additional_info, null, 2)}</pre>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className={styles['duty-details-actions']}>
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/duty-records/${id}`)}
          >
            العودة لسجل الواجبات
          </button>
          
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/lessonDetails/${id}`)}
          >
            العودة لتفاصيل الحصة
          </button>
        </div>
      </div>
    </div>
  );
}

export default DutyDetails;
